import React, { useState, useEffect } from "react";
import { Box, Step, StepLabel, Stepper, Typography, useTheme, useMediaQuery, StepConnector } from '@mui/material';
import { styled } from '@mui/material/styles';
import {makeStyles} from "@mui/styles";
import CheckIcon from '@mui/icons-material/Check';
import PatientDetails from "../patientDetails/patientDetails";
import HealthInformation from "../healthInformation/healthInformation";
import ImageUpload from "../imageUpload/imageUpload";
import ReviewDetails from "../reviewDetails/reviewDetails";
import axios from "axios";
import config from "../../config";
import { handleGetUser } from "../../utils/patientUtils";
import { useLocation, useNavigate } from "react-router-dom";
import { db } from "../../db";
import { normalizeModelTrainingConsent } from "../../utils/modelTrainingConsent";


const StyledStepConnector = styled(StepConnector)(({ theme }) => ({
  '& .MuiStepConnector-line': {
    borderTopWidth: 2,
    borderColor: 'rgba(148, 148, 148, 0.3)',
    minWidth: 50,
    flex: 1,
  },
}));

const useStyles = makeStyles(() => ({
  StepLabel: {
    fontWeight: 500,
    fontSize: "1.2rem !important",
    color: "rgba(148, 148, 148, 1)",
    whiteSpace: "nowrap !important",
    overflow: "visible !important",
  },
  stepIconRoot: {
    color: "rgba(148, 148, 148, 1)",
    width: 28,
    height: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
    border: "2px solid rgba(148, 148, 148, 0.5)",
    borderRadius: "50%",
    backgroundColor: "transparent",
  },
  activeStepIcon: {
    backgroundColor: "#333",
    color: "#fff",
    border: "none",
  },
  completedStepIcon: {
    backgroundColor: "#4CAF50",
    color: "#fff",
    border: "none",
  },
  stepIconText: {
    fontSize: 16,
    fontWeight: "bold",
  },
}));

const CustomStepIcon = (props) => {
  const { active, completed, icon } = props;
  const classes = useStyles();

  if (completed) {
    return (
      <div className={`${classes.stepIconRoot} ${classes.completedStepIcon}`}>
        <CheckIcon style={{ fontSize: 16 }} />
      </div>
    );
  }
  return (
    <div
      className={`${classes.stepIconRoot} ${active ? classes.activeStepIcon : ""}`}
    >
      <span className={classes.stepIconText}>{icon}</span>
    </div>
  );
};

function getStepContent(
  formik,
  step,
  handleNext,
  handleBack,
  handleSubmit,
  handleAddPatient,
  handleGetUser,
  handleUpdateUser,
  findPatient,
  setFindPatient,
  idType,
  setIdType,
  idValue,
  setIdValue,
  setIsSearching,
  setActiveStep,
  handleSubmitReport,
  setAutoSubmitAfterInference,
  imageUploadSessionKey
) {
  switch (step) {
    case 0:
      return (
        <PatientDetails
          handleNext={handleNext}
          formik={formik}
          handleAddPatient={handleAddPatient}
          handleGetUser={handleGetUser}
          handleUpdateUser={handleUpdateUser}
          findPatient={findPatient}
          setFindPatient={setFindPatient}
          idType={idType}
          setIdType={setIdType}
          idValue={idValue}
          setIdValue={setIdValue}
          setIsSearching={setIsSearching}
        />
      );
    case 1:
    return (
        <ImageUpload
            key={`image-upload-${imageUploadSessionKey}`}
            handleBack={handleBack}
            handleSubmit={handleSubmit}
            formik={formik}
            setActiveStep={setActiveStep}
        />
      );
    case 2:
      return (
        <ReviewDetails
          formik={formik}
          handleEdit={() => setActiveStep(0)}
          handleSubmit={handleSubmit}
          handleSubmitReport={handleSubmitReport}
          setAutoSubmitAfterInference={setAutoSubmitAfterInference}
        />
      );
    default:
      throw new Error("Unknown step");
  }
};

function getHeadContent(step, isMobile) {
    
    switch (step) {
        case 0:
            return "Patient Details";
        case 1:
            return "Image Capture";
        case 2:
            return "Review Details";
        default:
             throw new Error('Unknown Step');
    }
}


const PatientForm = ({formik, steps, activeStep, setActiveStep, handleSubmit, handleSubmitReport, setAutoSubmitAfterInference, imageUploadSessionKey = 0}) => {
    const classes = useStyles();
    const header = {"Authorization": "Token "+localStorage.getItem("optha-token")};
    const [findPatient, setFindPatient] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [idType, setIdType] = useState("other_id");
    const [idValue, setIdValue] = useState();
    const [isEditMode, setIsEditMode] = useState(false);
    const [editReportId, setEditReportId] = useState(null);
    const [editEye, setEditEye] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Check for edit mode from URL parameters
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const edit = searchParams.get('edit');
        const reportId = searchParams.get('reportId');
        const eye = searchParams.get('eye');
        const step = searchParams.get('step');

        if (edit === 'true' && reportId) {
            setIsEditMode(true);
            setEditReportId(reportId);
            setEditEye(eye);
            
            // Set the initial step if specified
            if (step) {
                setActiveStep(parseInt(step));
            }
            
            // Load existing data for editing
            loadReportData(reportId);
        }
    }, [location.search, setActiveStep]);

    // Restore findPatient state and idValue on mount if patient_id exists in localStorage
    useEffect(() => {
        const patientIdInStorage = localStorage.getItem("patient_id");
        if (patientIdInStorage) {
            setFindPatient(true);
        }
        
        // Restore idValue from formik values if available
        const otherId = formik.values.pat_info?.otherId;
        if (otherId) {
            setIdValue(otherId);
        }
    }, []);

    const loadReportData = async (reportId) => {
        try {
            // Load report data from IndexDB
            const report = await db.reports.where('id').equals(reportId).first();
            if (report) {
                // Load patient data
                const patient = await db.patients.where('id').equals(report.patient_id).first();
                if (patient) {
                    // Pre-fill form with existing data
                    formik.setValues({
                        ...formik.values,
                        patient: {
                            name: patient.name || '',
                            age: patient.age || '',
                            sex: patient.sex || '',
                            other_id: patient.other_id || '',
                            abha_id: patient.abha_id || ''
                        },
                        diabetes: report.diabetes || '',
                        diabetesDurationYears: '',
                        diabetesDurationMonths: '',
                        visualAcuity: {
                            left: report.vision_issues ? (report.vision_issues.match(/Left:\s*([^,]+)/)?.[1]?.trim() || '') : '',
                            right: report.vision_issues ? (report.vision_issues.match(/Right:\s*(.+)/)?.[1]?.trim() || '') : '',
                        },
                        single_image_reason: report.single_image_reason || '',
                        image_file: {
                            model_training_consent: normalizeModelTrainingConsent(report.model_training_consent),
                            left: {
                                image: report.left_image || null,
                                gradabale: report.ml_model?.gradability?.left || '',
                                reason: report.single_image_reason || '',
                                classification: report.ml_model?.classification?.left || {},
                                gradability: report.ml_model?.gradability?.left || {}
                            },
                            right: {
                                image: report.right_image || null,
                                gradabale: report.ml_model?.gradability?.right || '',
                                reason: report.single_image_reason || '',
                                classification: report.ml_model?.classification?.right || {},
                                gradability: report.ml_model?.gradability?.right || {}
                            }
                        }
                    });
                }
            }
        } catch (error) {
            console.error('Error loading report data for editing:', error);
        }
    };

    const handleAddPatient = async () => {
        try {
            const body = {
                // user: {
                //     first_name: formik.values.pat_info.name.split(' ')[0],
                //     last_name: formik.values.pat_info.name.split(' ').slice(1).join('_'),
                // },
                // name: formik.values.pat_info.name,
                age: formik.values.pat_info.age,
                sex: formik.values.pat_info.gender,
                other_id: formik.values.pat_info.otherId,
                // diabetes: formik.values.pat_info.diabetes,
                // vision_issues:formik.values.pat_info.vision_issues,
            };
            const response = await axios
            .post(`${config.API_BASE_URL}/api/auth/patients/create/`, body, {headers: header});
            if(response.data.id) {
                localStorage.setItem('patient_id', response.data.id);
                handleNext();
            }
        }
        catch (error) {
            alert(error.response.data?.[formik.values.pat_info.otherId]);
        }
    };

    const handleGetUserWrapper = async (patientId) => {
      try {
            const patient = await handleGetUser(patientId, idType);
            if (patient) {
              localStorage.setItem('patient_id', patient.id);
              setFindPatient(true);
              return patient;
          } else {
              setFindPatient(false);
              return null;
          }
      } catch (error) {
          // More detailed logging for the error
          console.error("Error fetching patient:", error);
          if (error.response) {
              alert(error.response.data.error)
          } else {
              console.error("Error Message:", error.message);
          }
          setFindPatient(false);
          return null;
      }
  };
  
    

    const handleUpdateUser = async ({patient_id}) => {
        const uid = formik.values.pat_info?.uniqueId;
        const idKey = uid?.idType ?? 'other_id';
        const idVal = uid?.idValue ?? formik.values.pat_info?.otherId ?? '';

        const body = {
            // user: {
            //     first_name: formik.values.pat_info.name.split(' ')[0],
            //     last_name: formik.values.pat_info.name.split(' ').slice(1).join('_'),
            // },
            // name: formik.values.pat_info.name,
            age: formik.values.pat_info.age,
            sex: formik.values.pat_info.gender,
            role: "patient",
            diabetes: formik.values.pat_info.diabetes,
            vision_issues: formik.values.pat_info.visualAcuity 
                ? `Left: ${formik.values.pat_info.visualAcuity.left || ''}, Right: ${formik.values.pat_info.visualAcuity.right || ''}`
                : '',
            [idKey]: idVal,
        };
        await axios
        .put(`${config.API_BASE_URL}/api/auth/patients/${patient_id}/update/`, body, {headers: header})
        .then((response) => {
            alert(`User Profile Updated!!!`);
            handleNext();
        })
        .catch((error) => {
            alert("Not able to update the Profile!!!");
            console.error(error);
        })
    }

    const handleNext = () => {
        setActiveStep(activeStep + 1);
    };

    const handleBack = () => {
        setActiveStep(activeStep - 1);
    };

  return (
    <div>
        <Box sx ={{marginX: isMobile ? "0vw" : "20vw"}} >
          <Box sx={{width: "100%", maxWidth: "100%"}}>
            {!isMobile && <Stepper 
              activeStep={activeStep} 
              connector={<StyledStepConnector />}
              sx={{ 
                padding: "20px 0",
                width: "100%",
                '& .MuiStep-root': { 
                  padding: '0 10px',
                  flex: 1,
                },
                '& .MuiStepLabel-root': {
                  width: "100%",
                },
                '& .MuiStepLabel-label': {
                  whiteSpace: "nowrap",
                  overflow: "visible",
                }
              }}
            >
            {steps.map((label, index) =>
                (
                <Step key={label} sx={{ flex: 1 }}>
                    <StepLabel StepIconComponent={CustomStepIcon} classes={{label: classes.StepLabel}}>{label}</StepLabel>
                </Step>
                ))
            }
            </Stepper>}
            </Box>
            <Box
                padding={isMobile?"16px":"24px"}
                border="1px solid rgba(0, 0, 0, 0.05)"
                borderRadius="8px"
                marginTop="15px"
            >
          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            alignItems={isMobile ? "flex-start" : "center"}
            gap="18px"
            marginBottom="16px"
          >
            <Typography fontWeight="500" fontSize="24px">
              {getHeadContent(activeStep, isMobile)}
            </Typography>

            {activeStep === 0 && (idValue?.trim() || formik.values.pat_info?.otherId) && !isSearching && navigator.onLine && (() => {
              // Check localStorage for follow-up flag
              const followUpFlag = localStorage.getItem("is_follow_up_patient");
              const isFollowUpFromStorage = followUpFlag === "true";
              const showFollowUp = findPatient || isFollowUpFromStorage;
              
              return (
                <Box
                  sx={{
                    backgroundColor: "#0D1E83",
                    color: "white",
                    padding: "4px 8px",
                    borderRadius: "12px",
                    fontSize: "14px",
                  }}
                >
                  {showFollowUp ? "Follow Up Patient" : "New Patient"}
                </Box>
              );
            })()}
          </Box>

                {getStepContent(formik, activeStep, handleNext, handleBack, handleSubmit, handleAddPatient, handleGetUserWrapper, handleUpdateUser, findPatient, setFindPatient, idType, setIdType, idValue, setIdValue, setIsSearching, setActiveStep, handleSubmitReport, setAutoSubmitAfterInference, imageUploadSessionKey)}
            </Box>
        </Box>
    </div>
  )
}

export default PatientForm;